import React from "react";
import { AlertCircle, Clock, RefreshCw, Send, SkipForward } from "lucide-react";
import { getCampaignMessageMetrics } from "../../utils/campaignDisplayUtils";

const METRIC_ITEMS = [
    {
        key: "pending",
        label: "Pending & Scheduled Messages",
        icon: Clock,
        className: "border-slate-200 bg-slate-50 text-slate-700",
    },
    {
        key: "processing",
        label: "Processing",
        icon: RefreshCw,
        className: "border-amber-200 bg-amber-50 text-amber-700",
    },
    {
        key: "sent",
        label: "Sent",
        icon: Send,
        className: "border-emerald-200 bg-emerald-50 text-emerald-700",
    },
    {
        key: "skipped",
        label: "Skipped",
        sublabel: "(unsubscribed)",
        icon: SkipForward,
        className: "border-indigo-100 bg-indigo-50 text-indigo-700",
    },
    {
        key: "failed",
        label: "Failed",
        icon: AlertCircle,
        className: "border-red-200 bg-red-50 text-red-700",
    },
];

const getPendingScheduleText = (
    nextStepInfo,
    metricCounts,
    totalMessageCount,
) => {
    if (
        nextStepInfo?.state === "completed" ||
        (totalMessageCount > 0 &&
            metricCounts.pending === 0 &&
            metricCounts.processing === 0)
    ) {
        return "All messages have been processed";
    }

    if (
        nextStepInfo?.hasNextStep &&
        nextStepInfo.delayDays !== null &&
        nextStepInfo.delayDays !== undefined
    ) {
        const delayDays = Number(nextStepInfo.delayDays);

        if (Number.isFinite(delayDays)) {
            return `Step ${nextStepInfo.nextStep} Scheduled in ${delayDays} ${
                delayDays === 1 ? "day" : "days"
            }`;
        }
    }

    if (nextStepInfo?.state === "final-step") {
        return "Last step in progress";
    }

    return "Next step scheduled: Not available";
};

const CampaignMetrics = ({ metrics = {}, nextStepInfo = null }) => {
    const messageMetrics = getCampaignMessageMetrics(metrics);
    const metricCounts = {
        pending: messageMetrics.pending,
        processing: messageMetrics.processing,
        sent: messageMetrics.sent,
        skipped: messageMetrics.skipped,
        failed: messageMetrics.failed,
    };
    const totalMessageCount = messageMetrics.totalMessages;
    const statusPercentages = {
        processing: messageMetrics.processingPercentage,
        sent: messageMetrics.sentPercentage,
        skipped: messageMetrics.skippedPercentage,
        failed: messageMetrics.failedPercentage,
    };

    return (
        <div>
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Message Status
            </h4>
            <div className="grid grid-cols-2 gap-3 xl:grid-cols-[1.5fr_repeat(4,minmax(0,1fr))]">
                {METRIC_ITEMS.map((item) => {
                    const Icon = item.icon;

                    return (
                        <div
                            key={item.key}
                            className={[
                                "min-w-0 rounded-lg border p-2.5 sm:p-3",
                                item.className,
                            ].join(" ")}
                        >
                            <div className="flex min-w-0 items-start gap-2">
                                <Icon
                                    size={14}
                                    className="mt-0.5 shrink-0"
                                    aria-hidden="true"
                                />
                                <div className="min-w-0 flex-1">
                                    <div className="flex min-w-0 items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <p className="min-w-0 whitespace-normal text-xs font-semibold uppercase leading-4">
                                                {item.label}
                                            </p>
                                            {item.sublabel && (
                                                <p className="text-[11px] font-medium normal-case leading-4 text-indigo-600">
                                                    {item.sublabel}
                                                </p>
                                            )}
                                        </div>
                                        {statusPercentages[item.key] !==
                                            undefined &&
                                            totalMessageCount > 0 && (
                                                <span className="shrink-0 text-xs font-semibold leading-4">
                                                    {Math.round(
                                                        statusPercentages[
                                                            item.key
                                                        ],
                                                    )}
                                                    %
                                                </span>
                                            )}
                                    </div>
                                    <p className="mt-1 text-base font-bold sm:text-lg">
                                        {item.key === "pending"
                                            ? `${metricCounts.pending} / ${totalMessageCount}`
                                            : metricCounts[item.key]}
                                    </p>
                                </div>
                            </div>
                            {item.key === "pending" && (
                                <p className="mt-0.5 text-[11px] font-medium normal-case leading-4 text-slate-500">
                                    {getPendingScheduleText(
                                        nextStepInfo,
                                        metricCounts,
                                        totalMessageCount,
                                    )}
                                </p>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default CampaignMetrics;
